import { graphql, useStaticQuery } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
// import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { StaticQueryNft } from '../../../api/graph'
import { GlassCard, Spinner } from '../../../components'
import { BalancesHeader } from '../../../components/Balances'
import Background from '../../../images/hero-bg.svg'
import { RootState } from '../../../store'
import {
  Loading,
  NftCollection,
  selectCBstatsLoading,
  selectNftCollection,
  selectOwnershipLoading,
} from '../../../store/web3/collection.state'
import { selectEthUsdRatio } from '../../../store/web3/unbind.side-effects'
import { useNftLoadingState } from '../CollectionDisplay/hooks'
import { TxModal } from '../Unbind/TxModal'
import { fEth, fEthUsd } from '../Unbind/utils'
import './index.scss'

interface Props {
  renderWith: (
    collection: NftCollection,
    showOwned: boolean,
    showBindingFailed: boolean,
  ) => React.ReactElement
}
export const Layout: React.FC<Props> = ({ renderWith }) => {
  const { t } = useTranslation()
  const { allBinderNft }: StaticQueryNft = useStaticQuery(graphql`
    query Nfts {
      allBinderNft {
        nodes {
          gatsbyPath(filePath: "/my-collection/{BinderNFT.tokenId}")
          tokenId
          localVideo {
            publicURL
            videoScreenshots(timestamps: ["0"]) {
              childImageSharp {
                gatsbyImageData(quality: 100, placeholder: BLURRED)
              }
            }
          }
          description
          external_url
          name
          max_quantity
          rarityType
          isSecret
          attributes {
            trait_type
            value
          }
        }
      }
    }
  `)
  const collection = useSelector<
    RootState,
    ReturnType<typeof selectNftCollection>
  >((state) => selectNftCollection(state, allBinderNft.nodes))
  const statsLoadingState = useSelector(selectCBstatsLoading)
  const ownerStatsLoadingState = useSelector(selectOwnershipLoading)
  const ownerLoadable = loadable(ownerStatsLoadingState)
  const statsLoadable = loadable(statsLoadingState)

  const [showOwned, setShowOwned] = useState(true)
  const [showBindingFailed, setShowBindingFailed] = useState(false)
  const { ownerStatsLoaded, statsLoaded } = useNftLoadingState()
  const ethUsdRatio = useSelector(selectEthUsdRatio)

  useEffect(() => {
    setShowOwned(ownerStatsLoaded)
  }, [ownerStatsLoaded])

  return (
    <>
      <TxModal />
      <BackgroundSection>
        <GridArea>
          <CardsControls>
            <PrizePool>
              <JackpotLabel>{t('Reserve Pool Value')}</JackpotLabel>
              <Jackpot>
                {statsLoadable(fEth(collection.collectionValue, 0))} ETH
              </Jackpot>
              <USDAmount>
                $
                {statsLoadable(
                  fEthUsd(collection.collectionValue, ethUsdRatio),
                )}{' '}
                USD
              </USDAmount>
            </PrizePool>
            <BalancesHeader />
          </CardsControls>

          <CollectionStats>
            <GlassCard>
              <CardTitle>{t('collectionStats')}</CardTitle>
              <StyledUl>
                <StyledLi>
                  {t('setCompletion')}{' '}
                  {ownerLoadable(collection.totalNftsOwned)}/
                  {statsLoadable(collection.totalNfts)}
                </StyledLi>
                <StyledLi>
                  {t('collectionValue')}{' '}
                  {ownerLoadable(fEth(collection.ownedCollectionValue, 4))}{' '}
                  {ownerLoadable('ETH', true)}
                </StyledLi>
                <StyledLi color="#F6851B">
                  {t('legendaries')}{' '}
                  {ownerLoadable(collection.ownedNftsByRarity.Legendary)}
                </StyledLi>
                <StyledLi color="#C173FF">
                  {t('ethereals')}{' '}
                  {ownerLoadable(collection.ownedNftsByRarity.Ethereal)}
                </StyledLi>
                <StyledLi color="#00FF38">
                  {t('mythics')}{' '}
                  {ownerLoadable(collection.ownedNftsByRarity.Mythic)}
                </StyledLi>
                <StyledLi color="#00D1FF">
                  {t('rares')}{' '}
                  {ownerLoadable(collection.ownedNftsByRarity.Rare)}
                </StyledLi>
                <StyledLi color="#ffffff">
                  {t('commons')}{' '}
                  {ownerLoadable(collection.ownedNftsByRarity.Common)}
                </StyledLi>
              </StyledUl>
            </GlassCard>
            <GlassCard>
              <CardTitle>{t('Total Unbound Copies Left')}</CardTitle>
              <StyledUl>
                <StyledLi color="#F6851B">
                  {t('legendaries')}{' '}
                  {statsLoadable(collection.nftsByRarity.Legendary)}
                </StyledLi>
                <StyledLi color="#C173FF">
                  {t('ethereals')}{' '}
                  {statsLoadable(collection.nftsByRarity.Ethereal)}
                </StyledLi>
                <StyledLi color="#00FF38">
                  {t('mythics')} {statsLoadable(collection.nftsByRarity.Mythic)}
                </StyledLi>
                <StyledLi color="#00D1FF">
                  {t('rares')} {statsLoadable(collection.nftsByRarity.Rare)}
                </StyledLi>
                <StyledLi color="#ffffff">
                  {t('commons')} {statsLoadable(collection.nftsByRarity.Common)}
                </StyledLi>
              </StyledUl>
            </GlassCard>

            <GlassCard>
              <CardTitle>{t('cbUnbound')}</CardTitle>
              <StyledUl>
                <StyledLi>{statsLoadable(collection.totalBurned)}</StyledLi>
              </StyledUl>
            </GlassCard>
            <CollectionControls>
              <StyledToggleGroup>
                <StyledLabel htmlFor="switchOwned">
                  {t('viewOwnedOnly')}
                </StyledLabel>
                <StyledCheckbox
                  checked={showOwned}
                  disabled={!ownerStatsLoaded}
                  id="switchOwned"
                  onChange={() => setShowOwned(!showOwned)}
                ></StyledCheckbox>
              </StyledToggleGroup>

              <StyledToggleGroup>
                <StyledLabel htmlFor="switchFailed">
                  {t('viewBindingFailed')}
                </StyledLabel>
                <StyledCheckbox
                  checked={showBindingFailed}
                  disabled={!statsLoaded}
                  id="switchFailed"
                  onChange={() => setShowBindingFailed(!showBindingFailed)}
                ></StyledCheckbox>
              </StyledToggleGroup>
            </CollectionControls>
          </CollectionStats>
          {renderWith(collection, showOwned, showBindingFailed)}
        </GridArea>
      </BackgroundSection>
    </>
  )
}

const USDAmount = styled.h2`
  color: #22a90c;
  font-weight: 400;
  font-size: 1.274rem;
  position: absolute;
  bottom: 1rem;
  right: 1rem;
`

const JackpotLabel = styled.h3`
  font-family: 'Open Sans';
  font-weight: 500;
  font-size: 1rem;
  color: hsl(180, 3%, 62%);
  position: absolute;
  top: 1rem;
  left: 1rem;
`

const Jackpot = styled.h2`
  text-align: center;
  font-size: 1.75rem;
  color: white;
`

const CollectionControls = styled.div`
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 1rem;
  width: 100%;
  display: grid;
  grid-template-rows: 1fr 1fr;
  row-gap: 1rem;
`

const StyledToggleGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const StyledLabel = styled.label`
  color: white;
  margin-right: 1rem;
  font-size: 1rem;
`

const StyledCheckbox = styled.input.attrs((props) => ({
  type: 'checkbox',
}))`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;

  height: 32px;
  width: 52px;
  border-radius: 16px;
  display: inline-block;
  position: relative;
  margin: 0;
  border: 2px solid ${(props) => (props.disabled ? '#9B9B9B' : 'white')};
  background: linear-gradient(180deg, #2d2f39 0%, #1f2027 100%);
  transition: all 0.2s ease;

  &:after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${(props) => (props.disabled ? '#9B9B9B' : 'white')};
    box-shadow: 0 1px 2px rgba(44, 44, 44, 0.2);
    transition: all 0.2s cubic-bezier(0.5, 0.1, 0.75, 1.35);
  }

  &:checked {
    border-color: #00ffc2;

    &:after {
      transform: translatex(20px);
      background: #00ffc2;
    }
  }
`

const PrizePool = styled.div`
  color: white;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 1rem;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  font-size: 1.5rem;

  position: relative;
`

const CardsControls = styled.div`
  @media (min-width: 220px) {
    display: grid;
    grid-template-rows: 1fr 1fr;
    gap: 1rem;
    grid-column: 1 / span 2;
    width: 100%;
    height: 100%;
    column-gap: 1rem;
    position: relative;
  }
  @media (min-width: 750px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;

    grid-column: 1 / span 2;
    width: 100%;
    height: 100%;
    column-gap: 1rem;
    position: relative;
  }

  @media (min-width: 1200px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column: 1 / span 2;
    width: 100%;
    height: 100%;
    column-gap: 1rem;
    position: relative;
  }
`

const StyledUl = styled.ul``

interface StyledCardLi {
  size?: any
  color?: any
}

const StyledLi = styled.li<StyledCardLi>`
  color: ${(props) => (props.color ? props.color : 'white')};
  padding-top: 5px;
  font-size: 1.075rem;

  margin-top: 1rem;
  margin-bottom: 1rem;

  @media (min-width: 200px) {
    font-size: ${(props) => (props.size ? props.size : '0.725rem')};
  }

  @media (min-width: 750px) {
    font-size: ${(props) => (props.size ? props.size : '1rem')};
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const CollectionStats = styled.div`
  @media (min-width: 250px) {
    grid-row-start: 2;
    grid-row-end: 3;
    display: grid;
    grid-template-rows: repeat(4, max-content);
    width: 100%;
    gap: 1rem;
  }
  @media (min-width: 750px) {
    grid-row-start: 2;
    grid-row-end: 3;
    grid-column-start: 1;
    grid-column-end: 3;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 1fr;
    column-gap: 1rem;
    height: 100%;
    width: 100%;
  }

  @media (min-width: 1200px) {
    grid-column-start: 1;
    grid-column-end: 2;
    display: grid;
    grid-template-rows: min-content min-content min-content min-content;
    grid-template-columns: 1fr;
    row-gap: 1rem;
    height: 100%;
  }
`

const CardTitle = styled.h3`
  font-family: 'Open Sans';
  font-weight: 500;
  font-size: 1rem;
  color: hsl(180, 3%, 62%);
`

const GridArea = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  grid-template-rows: min-content min-content 1fr;
  margin-right: 1rem;
  margin-left: 1rem;

  @media (min-width: 750px) {
    grid-template-columns: 1fr;
    grid-template-rows: max-content 1fr auto;
    margin-right: 1rem;
    margin-left: 1rem;
  }

  @media (min-width: 1200px) {
    height: calc(100% - 2rem);
    width: calc(100% - 2rem);

    margin-right: 1rem;
    margin-left: 1rem;

    grid-template-columns: max-content 1fr;
    grid-template-rows: 7rem 1fr;
    padding-right: 2.5rem;
    padding-left: 2.5rem;
  }

  @media (min-width: 12350px) {
    padding-right: 5rem;
    padding-left: 5rem;
    height: 100%;
  }
`

const BackgroundSection = styled.section`
  position: relative;

  padding-top: 5.5rem;
  background: url(${Background}) no-repeat center;
  -webkit-background-size: cover;
  -moz-background-size: cover;
  -o-background-size: cover;
  background-size: cover;
  width: 100%;
  position: relative;
`

function loadable(state: Loading) {
  return (prop: string | number, renderEmpty?: boolean) => {
    switch (state) {
      case Loading.IDLE:
      case Loading.ERRORED:
        return renderEmpty ? null : '-'
      case Loading.SUCCESSFUL:
        return prop || '0'
      case Loading.PENDING:
        return renderEmpty ? null : <Spinner size={1} />
    }
  }
}
